import { useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { useAuthStore } from '@/stores';
import { loginRequest, msalConfig, silentRequest } from '@/config/msal.config';
import { authService } from '@/services/auth/authService';
import { TokenStorage } from '@/services/auth/tokenStorage';

/**
 * Custom hook for MSAL authentication operations
 *
 * Provides methods for login, logout, and token acquisition
 */
export function useMsalAuth() {
  const { instance, accounts } = useMsal();
  const { clearMsalAuthentication } = useAuthStore();

  /**
   * Sign in with Microsoft using redirect flow
   */
  const signIn = useCallback(async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  }, [instance]);

  /**
   * Sign out locally while preserving Microsoft SSO session
   * - Calls backend logout API to revoke tokens
   * - Clears local MSAL cache and state
   * - Does NOT log out from Microsoft (preserves SSO for next login)
   */
  const signOut = useCallback(async () => {
    try {
      // Get MPN access token (not MSAL token) for backend logout API
      // The backend POST /logout expects an MPN access token via AdminApiAuthorizer
      const mpnToken =
        TokenStorage.getAccessToken() ||
        localStorage.getItem('mpn_access_token') ||
        undefined;

      // Call backend logout API to revoke tokens
      await authService.logout(mpnToken);

      // Clear Zustand state
      clearMsalAuthentication();

      // Clear MSAL cache locally (but keep Microsoft session for SSO)
      instance.setActiveAccount(null);

      // Clear ALL MSAL localStorage data
      // This removes all tokens and accounts from localStorage but preserves Microsoft's SSO cookies
      const clientId = msalConfig.auth.clientId;

      // Get all localStorage keys and remove MSAL-related ones
      Object.keys(localStorage).forEach(key => {
        if (key.includes('msal') || key.includes(clientId)) {
          localStorage.removeItem(key);
        }
      });

      // Navigate to login page manually (instead of Microsoft logout redirect)
      window.location.href = '/login';
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }, [instance, accounts, clearMsalAuthentication]);

  /**
   * Acquire an access token silently (for API calls)
   * Falls back to interactive login if silent acquisition fails
   */
  const getAccessToken = useCallback(async () => {
    if (accounts.length === 0) {
      throw new Error('No authenticated account found');
    }

    try {
      // Try silent token acquisition first
      const response = await instance.acquireTokenSilent({
        ...silentRequest,
        account: accounts[0],
      });

      return response.accessToken;
    } catch (error) {
      console.warn(
        'Silent token acquisition failed, falling back to interactive',
        error
      );

      // Fall back to interactive login (this will redirect the browser)
      await instance.acquireTokenRedirect({
        ...silentRequest,
        account: accounts[0],
      });

      // This line won't be reached as acquireTokenRedirect redirects the browser
      throw new Error('Token acquisition requires interactive login');
    }
  }, [instance, accounts]);

  /**
   * Get the current user's account information
   */
  const getAccount = useCallback(() => {
    return accounts[0] || null;
  }, [accounts]);

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = accounts.length > 0;

  return {
    signIn,
    signOut,
    getAccessToken,
    getAccount,
    isAuthenticated,
  };
}
